/**
 * Hours of Rest Compliance Helper Functions
 * Extracted from HoursOfRestModule to reduce file size
 *
 * FT-30 (2026-03-19): WBC3 seagoing only, removed inland logic
 * - Fixed weeklyRest += 24 bug (missing data ≠ full rest)
 * - Removed horRegime/inland branches
 * - Updated getComplianceSummary() return shape
 */

// Get last 7 days for weekly compliance check
export const getLastWeekDates = () => {
  const dates = [];
  const today = new Date();
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    dates.push(date.toISOString().split('T')[0]);
  }
  return dates;
};

// Get current month key (YYYY-MM format)
export const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Get previous month key
export const getPreviousMonthKey = () => {
  const now = new Date();
  now.setMonth(now.getMonth() - 1);
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Get days elapsed in current month
export const getDaysElapsedInMonth = () => {
  return new Date().getDate();
};

// Calculate 7-day rest total for a crew member
// FT-156: Missing days assumed to be 24h rest (day off) to avoid false non-compliance
export const calculateWeeklyRest = (memberHours) => {
  const lastWeek = getLastWeekDates();
  let weeklyRest = 0;
  let daysWithData = 0;

  lastWeek.forEach(date => {
    const dayData = memberHours[date];
    if (dayData && typeof dayData.restHours === 'number') {
      weeklyRest += dayData.restHours;
      daysWithData++;
    } else {
      // FT-156: Missing day = assume 24h rest (day off)
      // This fixes false non-compliance for days without records
      weeklyRest += 24;
    }
  });

  return { weeklyRest, daysWithData };
};

// Get latest day's rest hours
export const getLatestDayRest = (memberHours) => {
  const lastWeek = getLastWeekDates();
  // Find most recent day with data
  for (let i = lastWeek.length - 1; i >= 0; i--) {
    const dayData = memberHours[lastWeek[i]];
    if (dayData && typeof dayData.restHours === 'number') {
      return { restHours: dayData.restHours, date: lastWeek[i] };
    }
  }
  return null;
};

// Get days recorded this month for a crew member
export const getDaysRecordedThisMonth = (memberHours) => {
  const monthKey = getCurrentMonthKey();
  const daysElapsed = getDaysElapsedInMonth();
  let daysRecorded = 0;

  Object.keys(memberHours).forEach(date => {
    if (date.startsWith(monthKey) && memberHours[date]) {
      daysRecorded++;
    }
  });

  return { daysRecorded, daysElapsed };
};

// Check for rolling 24-hour period violations (seagoing vessels)
export const checkRolling24HourCompliance = (memberHours, dates) => {
  const violations = [];
  
  // For each day, check if work periods create any 24-hour windows with < 10h rest
  for (let i = 0; i < dates.length - 1; i++) {
    const currentDate = dates[i];
    const nextDate = dates[i + 1];
    const currentData = memberHours[currentDate];
    const nextData = memberHours[nextDate];
    
    if (currentData && nextData && currentData.workEnd && nextData.workStart) {
      // Calculate rest period between work shifts
      const currentEnd = new Date(`${currentDate}T${currentData.workEnd}`);
      const nextStart = new Date(`${nextDate}T${nextData.workStart}`);
      
      // Handle overnight shifts
      if (currentData.workEnd < currentData.workStart) {
        currentEnd.setDate(currentEnd.getDate() + 1);
      }
      
      const restBetweenShifts = (nextStart - currentEnd) / (1000 * 60 * 60);
      
      // Check if this creates a 24-hour period with insufficient rest
      const workHoursInWindow = currentData.workHours + nextData.workHours;
      const restHoursInWindow = 24 - workHoursInWindow;
      
      if (restHoursInWindow < 10 && restBetweenShifts < 10) {
        violations.push({
          date: currentDate,
          issue: `Only ${restHoursInWindow.toFixed(1)}h rest from ${currentData.workEnd} to ${nextData.workStart}`
        });
      }
    }
  }
  
  return violations;
};

/**
 * Get compliance summary for all crew - NEW FT-30 return shape
 * Returns detailed breakdown for 4 compliance indicators
 *
 * @param {Array} crew - Crew members
 * @param {Object} hoursData - Hours data keyed by crew ID
 * @param {Object} signaturesData - Signatures data from useHoursOfRestData
 * @returns {Object} Compliance summary with 4 indicator groups
 */
export const getComplianceSummary = (crew, hoursData, signaturesData = {}) => {
  // Show all non-archived crew (includes both On Duty and Off Duty)
  const activeCrew = crew.filter(c => c.status !== 'archived');

  // Initialize counters for new return shape
  const sevenDayRest = { compliant: 0, warning: 0, violation: 0 };
  const dailyRest = { compliant: 0, violation: 0 };
  const recording = { complete: 0, gaps: 0, missing: 0 };

  const currentMonthKey = getCurrentMonthKey();
  const previousMonthKey = getPreviousMonthKey();

  activeCrew.forEach(member => {
    const memberHours = hoursData[member.id] || {};
    const lastWeek = getLastWeekDates();

    // FT-30: Fixed - no weeklyRest += 24 for missing days
    const { weeklyRest, daysWithData } = calculateWeeklyRest(memberHours);

    // Check 7-day rest compliance (WBC3: min 77h)
    // FT-156: Missing days assumed as 24h rest, so weeklyRest is always valid
    if (weeklyRest >= 77) {
      // 77h+ = compliant (includes assumed 24h for missing days)
      sevenDayRest.compliant++;
    } else if (weeklyRest >= 70 && weeklyRest < 77) {
      // Warning zone: close to limit
      sevenDayRest.warning++;
    } else {
      sevenDayRest.violation++;
    }

    // Check daily rest (last 24h) - WBC3: min 10h
    // FT-165: No data = 24h rest assumed (day off = compliant)
    const latestDay = getLatestDayRest(memberHours);
    if (!latestDay || latestDay.restHours >= 10) {
      dailyRest.compliant++;
    } else {
      dailyRest.violation++;
    }

    // Check recording completeness for current month
    const { daysRecorded, daysElapsed } = getDaysRecordedThisMonth(memberHours);
    const gaps = daysElapsed - daysRecorded;

    if (gaps === 0) {
      recording.complete++;
    } else if (gaps <= 2) {
      recording.gaps++;
    } else {
      recording.missing++;
    }
  });

  // Check signature status
  const currentSigs = signaturesData[currentMonthKey] || {};
  const previousSigs = signaturesData[previousMonthKey] || {};

  const signatures = {
    signed: !!(currentSigs.master && currentSigs.owner),
    previousMonthSigned: !!(previousSigs.master && previousSigs.owner)
  };

  // Also return legacy format for backward compatibility
  const compliant = sevenDayRest.compliant;
  const nonCompliant = sevenDayRest.warning + sevenDayRest.violation;

  return {
    // New FT-30 format
    sevenDayRest,
    dailyRest,
    recording,
    signatures,
    // Legacy format for backward compatibility
    compliant,
    nonCompliant,
    total: activeCrew.length
  };
};

// Get weekly statistics
export const getWeeklyStats = (activeCrew, hoursData) => {
  const weekDates = getLastWeekDates();
  let totalHoursWorked = 0;
  let crewCount = 0;
  
  activeCrew.forEach(member => {
    const memberHours = hoursData[member.id] || {};
    let memberWeeklyWork = 0;
    
    weekDates.forEach(date => {
      const dayData = memberHours[date];
      if (dayData && dayData.workHours) {
        memberWeeklyWork += dayData.workHours;
      }
    });
    
    if (memberWeeklyWork > 0) {
      totalHoursWorked += memberWeeklyWork;
      crewCount++;
    }
  });
  
  const compliance = getComplianceSummary(activeCrew, hoursData);
  
  return {
    avgHoursWorked: crewCount > 0 ? Math.round(totalHoursWorked / crewCount) : 0,
    complianceRate: compliance.total > 0 ? Math.round((compliance.compliant / compliance.total) * 100) : 100
  };
};

/**
 * Get crew members with compliance issues
 * FT-156: WBC3 seagoing only, missing days = 24h rest assumed
 */
export const getCrewWithIssues = (activeCrew, hoursData) => {
  return activeCrew.filter(member => {
    const memberHours = hoursData[member.id] || {};
    const lastWeek = getLastWeekDates();

    // FT-156: Missing days now assumed as 24h rest
    const { weeklyRest } = calculateWeeklyRest(memberHours);

    // Check for daily violations (only on recorded days)
    let hasViolation = false;
    lastWeek.forEach(date => {
      const dayData = memberHours[date];
      if (dayData && dayData.restHours < 10) {
        hasViolation = true;
      }
    });

    // Check rolling 24-hour periods
    const rollingViolations = checkRolling24HourCompliance(memberHours, lastWeek);

    // Crew has issues if:
    // - Weekly rest below 77h (includes assumed 24h for missing days)
    // - Any daily violation (< 10h)
    // - Any rolling 24h violation
    return weeklyRest < 77 || hasViolation || rollingViolations.length > 0;
  });
};